import dynamic from 'next/dynamic';

import { type StoryblokRichTextData } from '../../../../richtext';
import {
  getAssetFromStoryblok,
  getLinkPropsFromStoryblok,
  sbEditable,
  type SBProps,
  type StoryblokAsset,
  type StoryblokLink,
} from '../../../../utils';

const Author = dynamic(() => import('@natu/ui/Author').then(mod => mod.Author));

const StoryblokRichtext = dynamic(() =>
  import('../../../../richtext').then(mod => mod.StoryblokRichtext),
);

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

interface SBContactInfoProps {
  testimony?: StoryblokRichTextData;
  portrait?: StoryblokAsset;
  author?: string;
  position?: string;
  clutch?: StoryblokAsset;
  phoneLabel?: string;
  phoneDisplay?: string;
  phoneUrl?: StoryblokLink;
  emailLabel?: string;
  emailUrl?: StoryblokLink;
  emailDisplay?: string;
}

export const SBContactInfo = ({ blok }: SBProps<SBContactInfoProps>) => {
  const {
    author,
    clutch,
    emailDisplay,
    emailLabel,
    emailUrl,
    phoneDisplay,
    phoneLabel,
    phoneUrl,
    portrait,
    position,
    testimony,
  } = blok;

  const resolvedPortrait = getAssetFromStoryblok(portrait);
  const resolvedClutchImage = getAssetFromStoryblok(clutch);
  const resolvedPhoneUrl = getLinkPropsFromStoryblok(phoneUrl);
  const resolvedEmailUrl = getLinkPropsFromStoryblok(emailUrl);

  return (
    <div {...sbEditable(blok)}>
      {testimony && (
        <StoryblokRichtext className="mb-8 text-xl text-white xl:text-2xl" data={testimony} />
      )}
      {(portrait || author || position) && (
        <Author image={resolvedPortrait} name={author} role={position} variant="contact" />
      )}
      {resolvedClutchImage.src && (
        <div className="border-primary-500 mb-8 border-y py-8">
          <ResponsiveImage {...resolvedClutchImage} />
        </div>
      )}
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-1">
          {phoneLabel && <p className="text-primary-200 text-sm font-semibold">{phoneLabel}</p>}
          {resolvedPhoneUrl && phoneDisplay && (
            <a
              href={resolvedPhoneUrl.href}
              className="text-2xl text-white hover:underline hover:underline-offset-4"
            >
              {phoneDisplay}
            </a>
          )}
        </div>
        <div className="flex flex-col gap-y-1">
          {emailLabel && <p className="text-primary-200 text-sm font-semibold">{emailLabel}</p>}
          {resolvedEmailUrl && emailDisplay && (
            <a
              href={resolvedEmailUrl.href}
              className="text-2xl text-white hover:underline hover:underline-offset-4"
            >
              {emailDisplay}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
